import { useCallback, useEffect, useState } from 'react';

/**
 * Where the panel sits, and how it gets out of its own way.
 *
 * The rule (plan, "Panel placement and occlusion"): the Guide must never cover
 * the element it is highlighting. FieldFlo's widget — the reference for this
 * work — fails exactly here: in Michael's screenshots it hides the form field
 * below it, and its article view covers most of the window. A panel that size
 * cannot coexist with "show me where".
 *
 * The decisions live here, separate from the rendering, so they can be tested
 * against injected geometry. jsdom reports zero-sized rects, so asserting on
 * real layout is not an option; asserting on the DECISION is, and that is where
 * a regression would be.
 */

export type PanelMode = 'side' | 'sheet';
export type DockSide = 'left' | 'right';
/** 'peek' is the collapsed bar a sheet becomes while something is highlighted. */
export type SheetSize = 'full' | 'peek';

const DOCK_KEY = 'demoops.guide.dock';
export const PANEL_WIDTH = 380;
export const PEEK_HEIGHT = 96;

export interface Placement {
  mode: PanelMode;
  dock: DockSide;
  sheetSize: SheetSize;
  /** Pixels of chrome at the bottom of the viewport the panel must clear. */
  bottomInset: number;
  /** Pixels of chrome at the TOP the panel must clear (the shell's top row). */
  topInset: number;
}

/**
 * Should a right-docked panel move to the left to clear this target?
 *
 * Pure, and exported for tests. The daily-log form is a centred `max-w-2xl`
 * column, so on a wide screen one side is always free — this only has to pick
 * the free one.
 */
export function dockAwayFrom(
  targetRect: { left: number; right: number } | null,
  viewportWidth: number,
  current: DockSide,
  panelWidth = PANEL_WIDTH,
): DockSide {
  if (!targetRect) return current;
  const panelLeftEdge = current === 'right' ? viewportWidth - panelWidth : 0;
  const panelRightEdge = current === 'right' ? viewportWidth : panelWidth;
  const overlaps = targetRect.right > panelLeftEdge && targetRect.left < panelRightEdge;
  if (!overlaps) return current;

  // Flip only if the other side actually has room for the target.
  const roomIfLeftDocked = viewportWidth - panelWidth - targetRect.right;
  const roomIfRightDocked = targetRect.left - panelWidth;
  return current === 'right'
    ? (roomIfRightDocked >= 0 ? 'left' : current)
    : (roomIfLeftDocked >= 0 ? 'right' : current);
}

/**
 * Pixels at the top of the window the panel must start below.
 *
 * The shell's own top row first (compact chrome, 2026-10-10): it holds the
 * bell, NADC apps and the connection light, and a panel from the top of the
 * window would cover all three while the Guide is open. Both shells mark
 * their header `data-guide-chrome="top-bar"`; the panel starts at its
 * bottom, which counts whatever sits above the row too (the preview band).
 * That is also where <main> starts, so it is the top of the safe area.
 *
 * Else the shared NADC portal navbar, which is `position: fixed` at the top
 * of every page and is NOT part of this repo — hence measuring `.nadc-navbar`
 * rather than tagging it. A panel at top:0 slides its own header underneath it
 * (found on the live site, 2026-08-12). Since 2026-10-09 DemoOps does not
 * load the bar (Michael, to save screen space); kept, not deleted, so the bar
 * can come back without the panel sliding under it again.
 */
export function measureTopChrome(): number {
  if (typeof document === 'undefined') return 0;
  for (const row of document.querySelectorAll('[data-guide-chrome="top-bar"]')) {
    const rect = row.getBoundingClientRect();
    // Drawn, and at the top: under the preview band and the NADC bar at most.
    if (rect.height > 0 && rect.top <= 160) return rect.bottom;
  }
  const nav = document.querySelector('.nadc-navbar');
  if (!nav) return 0;
  const rect = nav.getBoundingClientRect();
  return rect.height > 0 && rect.top <= 2 ? rect.height : 0;
}

/** Height of the app's bottom navigation, which the panel must sit above. */
export function measureBottomChrome(): number {
  if (typeof document === 'undefined') return 0;
  const nav = document.querySelector('[data-guide-chrome="bottom-nav"]');
  if (!nav) return 0;
  const rect = nav.getBoundingClientRect();
  // Only counts if it is actually pinned to the bottom of the viewport.
  return rect.height > 0 && rect.bottom >= window.innerHeight - 2 ? rect.height : 0;
}

export function usePanelPlacement(spotlighting: boolean): Placement & {
  setDock: (side: DockSide) => void;
  avoid: (rect: { left: number; right: number } | null) => void;
} {
  const [viewportWidth, setViewportWidth] = useState(
    () => (typeof window === 'undefined' ? 1280 : window.innerWidth),
  );
  const [dock, setDockState] = useState<DockSide>(() => {
    if (typeof localStorage === 'undefined') return 'right';
    return localStorage.getItem(DOCK_KEY) === 'left' ? 'left' : 'right';
  });
  const [bottomInset, setBottomInset] = useState(0);
  const [topInset, setTopInset] = useState(0);

  useEffect(() => {
    const onResize = () => {
      setViewportWidth(window.innerWidth);
      setBottomInset(measureBottomChrome());
      setTopInset(measureTopChrome());
    };
    onResize();
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const setDock = useCallback((side: DockSide) => {
    setDockState(side);
    try { localStorage.setItem(DOCK_KEY, side); } catch { /* private mode */ }
  }, []);

  const mode: PanelMode = viewportWidth >= 1024 ? 'side' : 'sheet';

  // A sheet cannot dodge sideways — it spans the width — so it collapses to a
  // peek bar instead. A side panel keeps its content and moves.
  const sheetSize: SheetSize = spotlighting ? 'peek' : 'full';

  const avoid = useCallback(
    (rect: { left: number; right: number } | null) => {
      if (mode !== 'side') return;
      setDockState((current) => {
        const next = dockAwayFrom(rect, window.innerWidth, current);
        // Deliberately NOT persisted: dodging is a transient reaction to one
        // target, not the user choosing a side.
        return next;
      });
    },
    [mode],
  );

  return { mode, dock, sheetSize, bottomInset, topInset, setDock, avoid };
}
